import React, { useState, useEffect } from 'react';
import { X, Plus, Edit, Trash2, Save, Percent, CreditCard, DollarSign, Smartphone, Building2, FileText, Receipt } from 'lucide-react';
import paymentConditionsService from '../services/paymentConditionsService';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const PaymentConditionsManager = ({ onClose }) => {
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCondition, setEditingCondition] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    payment_type: 'cash',
    affects_price: false,
    discount_percentage: 0,
    increase_percentage: 0,
    card_brand: null,
    installments: 1,
    has_interest: false,
    interest_rate_per_installment: 0,
    display_order: 0,
    is_active: true,
    rate_paid_by: 'cliente'
  });

  const paymentTypes = {
    cash: { label: 'Dinheiro', icon: DollarSign },
    cash_installments: { label: 'Dinheiro Parcelado', icon: DollarSign },
    pix: { label: 'PIX', icon: Smartphone },
    credit_card: { label: 'Cartão de Crédito', icon: CreditCard },
    credit_card_installments: { label: 'Cartão Parcelado', icon: CreditCard },
    debit_card: { label: 'Cartão de Débito', icon: CreditCard },
    bank_transfer: { label: 'Transferência Bancária', icon: Building2 },
    bank_transfer_installments: { label: 'Transferência Parcelada', icon: Building2 },
    boleto: { label: 'Boleto', icon: FileText },
    boleto_installments: { label: 'Boleto Parcelado', icon: FileText },
    cheque: { label: 'Cheque', icon: Receipt },
    cheque_installments: { label: 'Cheque Parcelado', icon: Receipt },
    cheque_predate: { label: 'Cheque Pré-datado', icon: Receipt },
    custom: { label: 'Personalizado', icon: DollarSign }
  };

  const cardBrands = [
    { value: 'all', label: 'Todas as bandeiras' },
    { value: 'DEBIT', label: 'Débito' },
    { value: 'CREDIT', label: 'Crédito' },
    { value: 'VISA', label: 'Visa' },
    { value: 'MASTERCARD', label: 'Mastercard' },
    { value: 'ELO', label: 'Elo' },
    { value: 'AMEX', label: 'American Express' },
    { value: 'HIPERCARD', label: 'Hipercard' }
  ];

  useEffect(() => {
    loadConditions();
  }, []);

  const loadConditions = async () => {
    try {
      setLoading(true);
      const response = await paymentConditionsService.getAllConditions({ is_active: true });
      setConditions(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar condições:', error);
      toast.error('Erro ao carregar condições: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (condition) => {
    setEditingCondition(condition);
    setFormData({
      id: condition.id,
      name: condition.name,
      description: condition.description || '',
      payment_type: condition.payment_type,
      affects_price: condition.affects_price || false,
      discount_percentage: condition.discount_percentage || 0,
      increase_percentage: condition.increase_percentage || 0,
      card_brand: condition.card_brand || null,
      installments: condition.installments || 1,
      has_interest: condition.has_interest || false,
      interest_rate_per_installment: condition.interest_rate_per_installment || 0,
      display_order: condition.display_order || 0,
      is_active: condition.is_active,
      rate_paid_by: condition.rate_paid_by || 'cliente'
    });
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingCondition(null);
    setFormData({
      name: '',
      description: '',
      payment_type: 'cash',
      affects_price: false,
      discount_percentage: 0,
      increase_percentage: 0,
      card_brand: null,
      installments: 1,
      has_interest: false,
      interest_rate_per_installment: 0,
      display_order: 0,
      is_active: true
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCondition) {
        await paymentConditionsService.upsertCondition(formData);
        toast.success('Condição atualizada com sucesso!');
      } else {
        await paymentConditionsService.upsertCondition(formData);
        toast.success('Condição criada com sucesso!');
      }
      
      setShowForm(false);
      setEditingCondition(null);
      resetForm();
      loadConditions();
    } catch (error) {
      console.error('Erro ao salvar condição:', error);
      toast.error('Erro ao salvar condição: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = (id) => {
    setConfirmMessage('Tem certeza que deseja desativar esta condição de pagamento?');
    setConfirmAction(() => async () => {
      try {
        await paymentConditionsService.deleteCondition(id);
        toast.success('Condição desativada com sucesso!');
        loadConditions();
      } catch (error) {
        console.error('Erro ao deletar condição:', error);
        toast.error('Erro ao deletar condição: ' + (error.response?.data?.message || error.message));
      }
    });
    setShowConfirmModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      payment_type: 'cash',
      affects_price: false,
      discount_percentage: 0,
      increase_percentage: 0,
      card_brand: null,
      installments: 1,
      has_interest: false,
      interest_rate_per_installment: 0,
      display_order: 0,
      is_active: true,
      rate_paid_by: 'cliente'
    });
  };

  const formatCondition = (condition) => {
    const parts = [];
    
    if (condition.affects_price) {
      if (condition.discount_percentage > 0) {
        parts.push(`Desconto: ${condition.discount_percentage}%`);
      } else if (condition.increase_percentage > 0) {
        parts.push(`Acréscimo: ${condition.increase_percentage}%`);
      }
    }
    
    // Tipos de pagamento que mostram parcelas
    const installmentTypes = ['credit_card', 'credit_card_installments', 'cash_installments', 'bank_transfer_installments', 'boleto_installments', 'cheque_installments', 'cheque_predate'];

    if (installmentTypes.includes(condition.payment_type)) {
      if (condition.installments > 1) {
        parts.push(`${condition.installments}x`);
        if (condition.has_interest) {
          parts.push(`Juros: ${condition.interest_rate_per_installment}% por parcela`);
        } else {
          parts.push('Sem juros');
        }
      }
      if (condition.card_brand && condition.card_brand !== 'all') {
        parts.push(`Bandeira: ${cardBrands.find(b => b.value === condition.card_brand)?.label || condition.card_brand}`);
      }
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'Não afeta o preço';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Condições de Pagamento</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure as opções de pagamento e como elas influenciam no valor do orçamento
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nova Condição
          </button>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Lista de Condições */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Efeito no Preço</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parcelas</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {conditions.map((condition) => {
                const TypeIcon = paymentTypes[condition.payment_type]?.icon || DollarSign;
                return (
                  <tr key={condition.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <TypeIcon className="w-5 h-5 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{condition.name}</div>
                          {condition.description && (
                            <div className="text-xs text-gray-500">{condition.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {paymentTypes[condition.payment_type]?.label || condition.payment_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCondition(condition)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {condition.installments || 1}x
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(condition)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(condition.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {conditions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhuma condição de pagamento cadastrada.
          </div>
        )}
      </div>

      {/* Modal de Formulário */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingCondition ? 'Editar Condição' : 'Nova Condição de Pagamento'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingCondition(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Condição *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Ex: À vista no dinheiro com desconto de 3%"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição (opcional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    rows="2"
                    placeholder="Descrição adicional sobre esta condição..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Pagamento *
                  </label>
                  <select
                    value={formData.payment_type}
                    onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  >
                    {Object.entries(paymentTypes).map(([value, config]) => (
                      <option key={value} value={value}>{config.label}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.affects_price}
                      onChange={(e) => setFormData({ ...formData, affects_price: e.target.checked })}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Afeta o valor do orçamento
                    </span>
                  </label>
                </div>

                {formData.affects_price && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Desconto (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.discount_percentage}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          discount_percentage: parseFloat(e.target.value) || 0,
                          increase_percentage: 0 // Zerar acréscimo se houver desconto
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Acréscimo (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.increase_percentage}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          increase_percentage: parseFloat(e.target.value) || 0,
                          discount_percentage: 0 // Zerar desconto se houver acréscimo
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  </>
                )}

                {/* Tipos de pagamento que permitem parcelas */}
                {['credit_card', 'credit_card_installments', 'cash_installments', 'bank_transfer_installments', 'boleto_installments', 'cheque_installments', 'cheque_predate'].includes(formData.payment_type) && (
                  <>
                    {/* Bandeira do cartão - só para tipos de cartão */}
                    {['credit_card', 'credit_card_installments'].includes(formData.payment_type) && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bandeira do Cartão
                          </label>
                          <select
                            value={formData.card_brand || 'all'}
                            onChange={(e) => setFormData({ ...formData, card_brand: e.target.value === 'all' ? null : e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          >
                            {cardBrands.map(brand => (
                              <option key={brand.value} value={brand.value}>{brand.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Quem paga a taxa do cartão */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quem paga a taxa do cartão?
                          </label>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="rate_paid_by"
                                value="cliente"
                                checked={formData.rate_paid_by === 'cliente'}
                                onChange={(e) => setFormData({ ...formData, rate_paid_by: e.target.value })}
                                className="border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <span className="text-sm text-gray-700">Cliente (com taxa)</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="rate_paid_by"
                                value="empresa"
                                checked={formData.rate_paid_by === 'empresa'}
                                onChange={(e) => setFormData({ ...formData, rate_paid_by: e.target.value })}
                                className="border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <span className="text-sm text-gray-700">Empresa (sem juros)</span>
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {formData.rate_paid_by === 'cliente'
                              ? 'A taxa de cartão será adicionada ao valor final.'
                              : 'A empresa absorve a taxa de cartão (popularmente chamado de "sem juros").'}
                          </p>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número de Parcelas
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        value={formData.installments}
                        onChange={(e) => setFormData({ ...formData, installments: parseInt(e.target.value) || 1 })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>

                    {formData.installments > 1 && (
                      <>
                        <div className="md:col-span-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.has_interest}
                              onChange={(e) => setFormData({ ...formData, has_interest: e.target.checked })}
                              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                              Tem juros
                            </span>
                          </label>
                        </div>

                        {formData.has_interest && (
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Taxa de Juros por Parcela (%)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.interest_rate_per_installment}
                              onChange={(e) => setFormData({ ...formData, interest_rate_per_installment: parseFloat(e.target.value) || 0 })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                              placeholder="0.00"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              A taxa será multiplicada pelo número de parcelas. Ex: 2% por parcela em 3x = 6% total
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 pt-4 border-t">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingCondition ? 'Atualizar' : 'Salvar'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingCondition(null);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <ConfirmModal
          message={confirmMessage}
          onConfirm={() => {
            confirmAction();
            setShowConfirmModal(false);
          }}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </div>
  );
};

export default PaymentConditionsManager;
